import api from './api';
import { toast } from 'sonner';

/**
 * Robust PDF download/preview helpers.
 *
 * - downloadPDF: forces a save dialog. Uses fetch with auth so the browser
 *   actually triggers a download (a.click() inside a fresh blob).
 * - previewPDF: opens the PDF inline in a new tab. The user can read it
 *   in the browser, zoom, then optionally save from there.
 *
 * Both fail loudly with a toast if the server didn't return a PDF.
 */

async function fetchPdfBlob(url) {
  // Always force-bypass cache to avoid stale blobs
  const res = await api.get(url, {
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
  const ct = res.headers['content-type'] || '';
  if (!ct.includes('application/pdf')) {
    // Try to read JSON detail from blob
    try {
      const text = await res.data.text();
      let detail = 'Server returned non-PDF response';
      try { detail = JSON.parse(text).detail || detail; } catch {} // eslint-disable-line
      throw new Error(detail);
    } catch (inner) {
      throw new Error(inner.message || 'Server returned non-PDF response');
    }
  }
  return new Blob([res.data], { type: 'application/pdf' });
}

export async function downloadPDF(url, filename) {
  try {
    const blob = await fetchPdfBlob(url);
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'document.pdf';
    a.style.display = 'none';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    // Give the browser ~1s to actually trigger the save dialog before revoking
    setTimeout(() => { URL.revokeObjectURL(blobUrl); a.remove(); }, 1500);
    toast.success('Download started');
    return true;
  } catch (e) {
    toast.error(e.message || 'Download failed');
    return false;
  }
}

export async function previewPDF(url) {
  try {
    // Re-fetch as inline so the Content-Disposition encourages browser preview.
    // We still serve the same blob though — it's the in-browser viewer that handles it.
    const inlineUrl = url.includes('?') ? `${url}&inline=true` : `${url}?inline=true`;
    const blob = await fetchPdfBlob(inlineUrl);
    const blobUrl = URL.createObjectURL(blob);
    const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
    if (!w) {
      toast.error('Pop-up blocked — allow pop-ups to preview the PDF');
      // Revoke later in case the user enables pop-ups and re-clicks
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
      return false;
    }
    // Revoke after 60s — gives time for the new tab to load the blob
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return true;
  } catch (e) {
    toast.error(e.message || 'Preview failed');
    return false;
  }
}
